import { ref } from 'vue'
import type { PhotoValueType } from '~/types/photo'

/**
 * 全站共用的相片放大 Dialog — 單例狀態 + imperative API。
 *
 * 想開放大時：
 * ```
 * const photoZoom = usePhotoZoom()
 * photoZoom.open(photoValue)
 * ```
 *
 * 對應的 overlay 元件 `<PhotoZoomOverlay />` 只掛一次（`app.vue`）；任何頁面 / 元件
 * 呼叫 `open()` 都會顯示同一份 dialog、避免每個使用點各自寫 Dialog + naturalSize 邏輯。
 */
const isOpen = ref(false)
const currentPhoto = ref<PhotoValueType>(null)

export function usePhotoZoom() {
  /** 帶著 photo 打開放大 dialog；photo 為空直接 no-op（沒東西可放大） */
  function open(photo: PhotoValueType) {
    if (!photo) return
    currentPhoto.value = photo
    isOpen.value = true
  }

  /**
   * 關 dialog — 只翻 isOpen，故意不清 currentPhoto。
   * 原因：reka-ui Dialog 有 ~150ms 淡出動畫，若同時 null out currentPhoto、
   * `<img v-if="url">` 會立刻 unmount → 動畫期間圖片突然消失有可見閃斷。
   * 影響：module-scope 會 pin 住最後開過的 photo object 直到下次 open 覆蓋，
   * 但 photo object 極小（string + 4~5 個 size URL）、SPA 生命週期內無實質 leak。
   */
  function close() {
    isOpen.value = false
  }

  return { isOpen, currentPhoto, open, close }
}
